import * as React from "react";
import { TwoLevelSidebar, type SidebarNavItem, type SidebarSectionKey } from "@/components/ui/sidebar-component";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import { resolveTenantDashboardPath } from "@/lib/tenantNavigation";
import { tenantPath } from "@/lib/urls";
import {
  Menu,
  Activity,
  LayoutDashboard,
  ShoppingCart,
  ChefHat,
  Package,
  Users,
  FileText,
  Settings,
  QrCode,
  DollarSign,
  Clock,
  Check,
  Languages,
  Loader2,
  LogOut,
  Store,
  Plug,
  CheckCircle,
  GraduationCap,
  Briefcase,
  CalendarClock,
  Boxes,
  CreditCard,
  Sparkles,
  Gift,
  Bot,
} from "lucide-react";
import { type LucideIcon } from "lucide-react";
import { formatUiVersionLabel, getActiveUiVersion } from "@/lib/uiVersion";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "@/components/LanguageProvider";
import { useTranslation } from "react-i18next";
import { useRestaurantSidebarInfo } from "@/features/restaurant/hooks/useRestaurantSidebarInfo";
import { useRetailSidebarInfo } from "@/features/retail/hooks/useRetailSidebarInfo";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useTenantProfileRealtime } from "@/hooks/useTenantProfileRealtime";
import { ThemeToggle } from "@/components/ThemeToggle";
import TenantSwitcher from "@/components/TenantSwitcher";
import { useTenantSlug, useTenantStore } from "@/stores/tenantStore";
import { useRushModeEnabled } from '@/hooks/useRushModeEnabled';
import type { TenantPermission } from "@/lib/auth/roles";
import { useAiStaffUsageSummary } from "@/features/aiStaff/hooks/useAiStaffAutomation";
import { useOnboardingFlow } from "@/features/onboarding/OnboardingFlowProvider";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

interface DashboardLayoutV3Props {
  children: React.ReactNode;
}

const isBackofficeReadOnly = (() => {
  const flag = import.meta.env.VITE_MAINTENANCE_BACKOFFICE as string | undefined;

  if (!flag) {
    return false;
  }

  return flag.toLowerCase() === "true";
})();

type NavItem = {
  label: string;
  labelKey?: string;
  to: string;
  icon: LucideIcon;
  patterns: RegExp[];
  contentKey: SidebarSectionKey;
  requiredPermission?: TenantPermission;
};

const NAV_ITEMS: NavItem[] = [
  {
    label: "Dashboard",
    to: "/dashboard",
    icon: LayoutDashboard,
    patterns: [/^\/$/, /^\/dashboard$/, /^\/r\/[^/]+\/dashboard/],
    contentKey: "dashboard",
  },
  {
    label: "Orders",
    to: "/orders",
    icon: ShoppingCart,
    patterns: [/^\/orders/, /^\/r\/[^/]+\/orders/],
    contentKey: "orders",
  },
  {
    label: "Digital Menu",
    to: "/menu",
    icon: Package,
    patterns: [/^\/menu/, /^\/r\/[^/]+\/menu/],
    contentKey: "menu",
  },
  {
    label: "Kitchen Display",
    to: "/kitchen",
    icon: ChefHat,
    patterns: [/^\/kitchen/, /^\/r\/[^/]+\/kitchen/],
    contentKey: "kitchen",
  },
  {
    label: "Retail",
    labelKey: "sidebar.sections.retail",
    to: "/retail",
    icon: Store,
    patterns: [/^\/retail(?:\/?$)/, /^\/r\/[^/]+\/retail(?:\/?$)/],
    contentKey: "retail",
  },
  {
    label: "Retail Inventory",
    labelKey: "sidebar.sections.retailInventory",
    to: "/retail/inventory",
    icon: Boxes,
    patterns: [/^\/retail\/inventory/, /^\/r\/[^/]+\/retail\/inventory/],
    contentKey: "retail",
  },
  {
    label: "Retail Checkout",
    labelKey: "sidebar.sections.retailCheckout",
    to: "/retail/checkout",
    icon: CreditCard,
    patterns: [/^\/retail\/checkout/, /^\/r\/[^/]+\/retail\/checkout/],
    contentKey: "retail",
  },
  {
    label: "Rush Mode",
    labelKey: "sidebar.sections.rushMode",
    to: "/ops/rush",
    icon: Activity,
    patterns: [/^\/ops\/rush/, /^\/r\/[^/]+\/ops\/rush/],
    contentKey: "operations",
  },
  {
    label: "QR Management",
    to: "/qr-codes",
    icon: QrCode,
    patterns: [/^\/qr-codes/, /^\/r\/[^/]+\/qr-codes/],
    contentKey: "qr-codes",
  },
  {
    label: "Expo",
    to: "/expo",
    icon: CheckCircle,
    patterns: [/^\/expo/, /^\/r\/[^/]+\/expo/],
    contentKey: "expo",
  },
  {
    label: "Integration Store",
    to: "/integration-store",
    icon: Store,
    patterns: [/^\/integration-store/, /^\/r\/[^/]+\/integration-store/],
    contentKey: "integration-store",
  },
  {
    label: "Integrations",
    to: "/integrations",
    icon: Plug,
    patterns: [/^\/integrations/, /^\/r\/[^/]+\/integrations/],
    contentKey: "integrations",
  },
  {
    label: "Customers",
    to: "/customers",
    icon: Users,
    patterns: [/^\/customers/, /^\/r\/[^/]+\/customers/],
    contentKey: "customers",
    requiredPermission: "customers.view",
  },
  {
    label: "Catering & Events",
    labelKey: "navigation.catering",
    to: "/catering/events",
    icon: CalendarClock,
    patterns: [/^\/catering\/events/, /^\/r\/[^/]+\/catering\/events/],
    contentKey: "calendar",
    requiredPermission: "catering.view",
  },
  {
    label: "Analytics",
    to: "/reports",
    icon: FileText,
    patterns: [/^\/reports(?:\/(?!rewards).*)?$/, /^\/r\/[^/]+\/reports(?:\/(?!rewards).*)?$/],
    contentKey: "reports",
  },
  {
    label: "Ad Intelligence",
    labelKey: "navigation.adIntelligence",
    to: "/autopilot",
    icon: Sparkles,
    patterns: [/^\/autopilot/, /^\/r\/[^/]+\/autopilot/],
    contentKey: "reports",
  },
  {
    label: "Reward Redemptions",
    labelKey: "navigation.rewardRedemptions",
    to: "/reports/rewards",
    icon: Gift,
    patterns: [/^\/reports\/rewards/, /^\/r\/[^/]+\/reports\/rewards/],
    contentKey: "reports",
  },
  {
    label: "Payments",
    to: "/payments",
    icon: DollarSign,
    patterns: [/^\/payments/, /^\/r\/[^/]+\/payments/],
    contentKey: "payments",
  },
  {
    label: "Payroll Bridge",
    to: "/payroll/owner",
    icon: Briefcase,
    patterns: [/^\/payroll\//, /^\/r\/[^/]+\/payroll\//],
    contentKey: "settings",
  },
  {
    label: "Training",
    to: "/training",
    icon: GraduationCap,
    patterns: [/^\/training/, /^\/r\/[^/]+\/training/],
    contentKey: "training",
  },
  {
    label: "AI Staff Copilot",
    labelKey: "navigation.aiStaffCopilot",
    to: "/ai-staff",
    icon: Bot,
    patterns: [/^\/ai-staff/, /^\/r\/[^/]+\/ai-staff/],
    contentKey: "staff",
  },
  {
    label: "Staff",
    to: "/staff",
    icon: Clock,
    patterns: [/^\/staff/, /^\/settings\/staff/, /^\/r\/[^/]+\/settings\/staff/],
    contentKey: "staff",
  },
  {
    label: "Settings",
    to: "/settings",
    icon: Settings,
    patterns: [/^\/settings(?!\/staff)/, /^\/r\/[^/]+\/settings(?!\/staff)/],
    contentKey: "settings",
  },
];

const DASHBOARD_ROUTE = "/dashboard";
const SIDEBAR_PINNED_STORAGE_KEY = "braise:sidebar:pinned.v1";
const SIDEBAR_ORDER_STORAGE_KEY = "braise:sidebar:order.v1";
const DEFAULT_SIDEBAR_ORDER = NAV_ITEMS.map((item) => item.to);
const KNOWN_SIDEBAR_IDS = new Set(DEFAULT_SIDEBAR_ORDER);

const reorderNavForInventoryDualTab = (items: NavItem[]): NavItem[] => {
  const inventoryItem = items.find((item) => item.to === "/retail/inventory");
  const checkoutItem = items.find((item) => item.to === "/retail/checkout");
  if (!inventoryItem && !checkoutItem) {
    return items;
  }
  const remaining = items.filter(
    (item) => item.to !== "/retail/inventory" && item.to !== "/retail/checkout",
  );
  const inserts = [inventoryItem, checkoutItem].filter(
    (item): item is NavItem => Boolean(item),
  );
  if (inserts.length === 0) {
    return items;
  }
  const ordersIndex = remaining.findIndex((item) => item.to === "/orders");
  if (ordersIndex === -1) {
    return [...inserts, ...remaining];
  }
  const next = [...remaining];
  next.splice(ordersIndex + 1, 0, ...inserts);
  return next;
};

const parseStoredStringArray = (rawValue: string | null): string[] | undefined => {
  if (!rawValue) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(rawValue);
    if (Array.isArray(parsed)) {
      return parsed.filter((value: unknown): value is string => typeof value === "string");
    }
  } catch {
    // Invalid persisted state; ignore.
  }
  return undefined;
};

const readInitialPinnedSidebarIds = (): string[] => {
  if (typeof window === "undefined") {
    return [];
  }
  return parseStoredStringArray(window.localStorage.getItem(SIDEBAR_PINNED_STORAGE_KEY)) ?? [];
};

const readInitialSidebarOrder = (navItems?: readonly NavItem[]): string[] => {
  const fallbackOrder = (navItems ?? NAV_ITEMS).map((item) => item.to);
  if (typeof window === "undefined") {
    return [...fallbackOrder];
  }
  const stored = parseStoredStringArray(window.localStorage.getItem(SIDEBAR_ORDER_STORAGE_KEY));
  if (!stored || stored.length === 0) {
    return [...fallbackOrder];
  }
  return stored;
};

const persistSidebarPreference = (key: string, value: string[]): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    if (import.meta.env?.DEV) {
      console.warn(`[dashboard-layout] failed to persist ${key}`, error);
    }
  }
};

const arraysEqual = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const LanguageToggle = () => {
  const { t } = useTranslation(["common", "dashboard", "orders", "auth", "retail"]);
  const { availableLanguages, language, resolvedLanguage, setLanguage } = useLanguage();

  const options = React.useMemo(
    () =>
      availableLanguages.map((code) => ({
        code,
        name: t(`language.options.${code}.name`),
        description: t(`language.options.${code}.description`),
      })),
    [availableLanguages, t],
  );

  const activeOption = options.find((option) => option.code === language);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={t("language.title")}
          title={t("language.title")}
          className="group inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200/70 bg-white/80 px-2 text-sm font-medium text-gray-700 shadow-sm transition-all duration-300 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-emerald-500/60 dark:border-gray-700/60 dark:bg-gray-900/80 dark:text-gray-200 dark:hover:bg-gray-800 sm:px-3"
        >
          <Languages className="h-4 w-4 text-emerald-500 transition-transform duration-300 group-data-[state=open]:scale-110 dark:text-emerald-400" />
          <span className="hidden sm:inline">{t("language.label")}</span>
          <span className="font-semibold uppercase tracking-wide sm:hidden">
            {(activeOption?.code ?? resolvedLanguage).toUpperCase()}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56 border-gray-200/70 bg-white/95 p-2 text-gray-700 backdrop-blur-md dark:border-gray-700/60 dark:bg-gray-900/95 dark:text-gray-200"
      >
        <DropdownMenuLabel className="px-3 pb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t("language.selectPrompt")}
        </DropdownMenuLabel>
        {options.map((option) => {
          const isActive = option.code === language;
          return (
            <DropdownMenuItem
              key={option.code}
              onClick={() => {
                setLanguage(option.code);
              }}
              className={cn(
                "group flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-200",
                isActive
                  ? "bg-emerald-50 text-emerald-700 shadow-sm dark:bg-emerald-500/10 dark:text-emerald-400"
                  : "text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 dark:hover:text-gray-50",
              )}
            >
              <div className="flex flex-col">
                <span className="font-semibold leading-tight">{option.name}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{option.description}</span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                    isActive
                      ? "border-emerald-200/60 bg-emerald-100/80 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300"
                      : "border-gray-200/70 bg-gray-100/60 text-gray-600 dark:border-gray-700/60 dark:bg-gray-800/50 dark:text-gray-300",
                  )}
                >
                  {option.code.toUpperCase()}
                </span>
                {isActive && <Check className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
const SignOutButton = () => {
  const { t } = useTranslation(["common", "dashboard", "orders", "auth", "retail"]);
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { toast } = useToast();
  const { reset: resetOnboardingFlow } = useOnboardingFlow();
  const [open, setOpen] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const isMountedRef = React.useRef(true);
  const signOutLabel = t("auth.signOut.title", { defaultValue: "Sign out" });

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (isProcessing) {
        return;
      }
      setOpen(nextOpen);
    },
    [isProcessing],
  );

  const handleConfirmSignOut = React.useCallback(() => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    setOpen(false);
    resetOnboardingFlow();
    navigate('/', { replace: true });

    void signOut()
      .then(() => {
        toast({
          title: t("auth.signOut.successTitle"),
          description: t("auth.signOut.successDescription"),
        });
      })
      .catch((error: unknown) => {
        console.error('[DashboardLayout] sign out failed:', error);
        toast({
          title: t("auth.signOut.errorTitle"),
          description: t("auth.signOut.errorDescription"),
          variant: "destructive",
        });
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsProcessing(false);
        }
      });
  }, [isProcessing, navigate, resetOnboardingFlow, signOut, t, toast]);

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={signOutLabel}
          title={signOutLabel}
          disabled={isProcessing}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200/70 bg-white/80 px-2 text-sm font-medium text-gray-700 shadow-sm transition-all duration-300 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-emerald-500/60 dark:border-gray-700/60 dark:bg-gray-900/80 dark:text-gray-200 dark:hover:bg-gray-800 sm:px-3"
        >
          {isProcessing ? (
            <Loader2 className="h-4 w-4 animate-spin text-emerald-500 dark:text-emerald-400" aria-hidden="true" />
          ) : (
            <LogOut className="h-4 w-4 text-emerald-500 dark:text-emerald-400" aria-hidden="true" />
          )}
          <span className="hidden sm:inline">{signOutLabel}</span>
          <span className="sr-only sm:hidden">{signOutLabel}</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="border-gray-200/70 bg-white/95 text-gray-700 dark:border-gray-700/60 dark:bg-gray-900/95 dark:text-gray-200">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t("auth.signOut.title")}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-gray-600 dark:text-gray-300">
            {t("auth.signOut.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:flex-row sm:justify-end sm:space-x-2 sm:space-y-0">
          <AlertDialogCancel disabled={isProcessing}>
            {t("auth.signOut.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmSignOut}
            disabled={isProcessing}
            className="inline-flex items-center gap-2"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <LogOut className="h-4 w-4" aria-hidden="true" />
            )}
            <span>{t("auth.signOut.confirm")}</span>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const SkipToContent = () => {
  const { t } = useTranslation(["common", "dashboard", "orders", "auth", "retail"]);

  return (
    <a
      href="#main-content"
      className="sr-only focus-visible:not-sr-only focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 absolute left-4 top-4 z-[60] rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-900 shadow-lg dark:bg-gray-900 dark:text-gray-100"
    >
      {t("accessibility.skipToContent")}
    </a>
  );
};

const DashboardLayoutChrome = ({ children }: DashboardLayoutV3Props): React.ReactElement => {
  const location = useLocation();
  const isPeekView = React.useMemo(() => {
    if (!location.search) {
      return false;
    }
    const params = new URLSearchParams(location.search);
    return params.get("view") === "peek";
  }, [location.search]);
  const navigate = useNavigate();
  const [isSidebarVisible, setIsSidebarVisible] = React.useState(false);
  const toggleSidebarVisibility = React.useCallback(() => {
    setIsSidebarVisible((prev) => !prev);
  }, []);
  const pathname = location.pathname;
  const mainRef = React.useRef<HTMLElement | null>(null);
  const { t } = useTranslation(["common", "dashboard", "orders", "auth", "retail"]);
  const { formatPercent } = useLocaleFormatter();
  const uiVersionLabel = React.useMemo(() => formatUiVersionLabel(getActiveUiVersion()), []);
  const isFirstRender = React.useRef(true);
  const restaurantSidebar = useRestaurantSidebarInfo();
  const retailSidebar = useRetailSidebarInfo();

  const isRetailExperience = React.useMemo(() => {
    if (!pathname) {
      return false;
    }
    if (/^\/retail(?:\/|$)/.test(pathname)) {
      return true;
    }
    return /^\/r\/[^/]+\/retail(?:\/|$)/.test(pathname);
  }, [pathname]);

  const sidebarSource = isRetailExperience ? retailSidebar : restaurantSidebar;
  const sidebarInfo = sidebarSource.info;
  const sidebarInfoPending = sidebarSource.isLoading || sidebarSource.isError;
  const sidebarRestaurantName = sidebarInfo.name;
  const sidebarStatusText = sidebarInfoPending
    ? "--"
    : t(`status.${sidebarInfo.status}`, {
        defaultValue: sidebarInfo.statusLabel ?? sidebarInfo.status ?? "Online",
      });
  const retailAssociatesText = !sidebarInfoPending
    ? t("retail.sidebar.staffActive", {
        defaultValue: "{{count}} associates • {{registers}} registers",
        count: sidebarInfo.activeAssociates ?? sidebarInfo.activeStaff ?? 0,
        registers: sidebarInfo.registerCount ?? 0,
      })
    : "--";
  const restaurantStaffText = !sidebarInfoPending
    ? t("sidebar.staffActive", { count: sidebarInfo.activeStaff })
    : "--";
  const sidebarStaffLabel = isRetailExperience
    ? t("retail.sidebar.staffLabel", { defaultValue: "Store team" })
    : t("sidebar.staffLabel");
  const sidebarStaffText = isRetailExperience ? retailAssociatesText : restaurantStaffText;
  const sidebarUptimeText = sidebarInfoPending
    ? "--"
    : formatPercent(sidebarInfo.uptimePercent / 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const sidebarAccountSubtitle = sidebarInfoPending
    ? t("sidebar.accountLoading", { defaultValue: "Loading workspace..." })
    : sidebarStatusText;
  const { sessionQuery: session, user, loading: authLoading } = useAuth();
  const tenantSlug = useTenantSlug();
  const rushModeEnabled = useRushModeEnabled();
  const inventoryDualTabEnabled = useTenantStore((state) => state.remoteConfig.inventoryDualTabEnabled);

  const navSource = React.useMemo(
    () => (inventoryDualTabEnabled ? reorderNavForInventoryDualTab(NAV_ITEMS) : NAV_ITEMS),
    [inventoryDualTabEnabled],
  );

  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
  const [isHeaderElevated, setIsHeaderElevated] = React.useState(false);
  const [pinnedSidebarIds, setPinnedSidebarIds] = React.useState<string[]>(readInitialPinnedSidebarIds);
  const [sidebarOrder, setSidebarOrder] = React.useState<string[]>(() => readInitialSidebarOrder(navSource));
  const navBaselineRef = React.useRef<string[]>(navSource.map((item) => item.to));
  const sessionPermissions = React.useMemo(() => session.data?.permissions ?? [], [session.data?.permissions]);

  const resolveTenantPath = React.useCallback(
    (target: string) => {
      if (!target.startsWith("/")) {
        return target;
      }
      if (target.startsWith("/r/")) {
        return target;
      }
      if (target.startsWith("/dashboard")) {
        return resolveTenantDashboardPath(target);
      }
      return tenantPath(tenantSlug, target);
    },
    [tenantSlug],
  );

  const hasPermission = React.useCallback(
    (permission: TenantPermission) => sessionPermissions.includes(permission),
    [sessionPermissions],
  );

  const payrollEnabled = hasPermission('payroll.bridge.manage');

  const filteredNavItems = React.useMemo(() => {
    return navSource.filter((item) => {
      if (!payrollEnabled && item.to === "/payroll/owner") {
        return false;
      }
      if (!rushModeEnabled && item.to === "/ops/rush") {
        return false;
      }
      if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
        return false;
      }
      return true;
    });
  }, [hasPermission, navSource, payrollEnabled, rushModeEnabled]);

  React.useEffect(() => {
    setPinnedSidebarIds((previous) => {
      const sanitized = previous.filter((id) => KNOWN_SIDEBAR_IDS.has(id) && id !== DASHBOARD_ROUTE);
      return arraysEqual(previous, sanitized) ? previous : sanitized;
    });
    const ordering = filteredNavItems.map((item) => item.to);
    setSidebarOrder((previous) => {
      const sanitized = previous.filter((id) => ordering.includes(id));
      const appended = ordering.filter((id) => !sanitized.includes(id));
      let combined = [...sanitized, ...appended];

      const previousBaseline = navBaselineRef.current;
      if (previousBaseline.length > 0) {
        const baselineFiltered = previousBaseline.filter((id) => ordering.includes(id));
        const baselineChanged = !arraysEqual(baselineFiltered, ordering);
        const sanitizedMatchesBaseline = arraysEqual(sanitized, baselineFiltered);
        if (baselineChanged && sanitizedMatchesBaseline) {
          combined = [...ordering];
        }
      }

      return arraysEqual(previous, combined) ? previous : combined;
    });
    navBaselineRef.current = ordering;
  }, [filteredNavItems]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea && event.storageArea !== window.localStorage) {
        return;
      }
      if (event.key === SIDEBAR_PINNED_STORAGE_KEY) {
        const next = parseStoredStringArray(event.newValue) ?? [];
        setPinnedSidebarIds((previous) => (arraysEqual(previous, next) ? previous : next));
      }
      if (event.key === SIDEBAR_ORDER_STORAGE_KEY) {
        const next = parseStoredStringArray(event.newValue) ?? navSource.map((item) => item.to);
        setSidebarOrder((previous) => (arraysEqual(previous, next) ? previous : next));
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [navSource]);

  React.useEffect(() => {
    persistSidebarPreference(SIDEBAR_PINNED_STORAGE_KEY, pinnedSidebarIds);
  }, [pinnedSidebarIds]);

  React.useEffect(() => {
    persistSidebarPreference(SIDEBAR_ORDER_STORAGE_KEY, sidebarOrder);
  }, [sidebarOrder]);

  const activeItem = React.useMemo(
    () =>
      (filteredNavItems.find((item) => item.patterns.some((pattern) => pattern.test(pathname))) ??
        filteredNavItems[0] ?? navSource[0]),
    [filteredNavItems, navSource, pathname],
  );

  const translateLabel = React.useCallback(
    (item: NavItem) => (item.labelKey ? t(item.labelKey, { defaultValue: item.label }) : item.label),
    [t],
  );

  const navItemMap = React.useMemo(() => {
    const map = new Map<string, NavItem>();
    for (const item of filteredNavItems) {
      map.set(item.to, item);
    }
    return map;
  }, [filteredNavItems]);

  // Fetch AI Staff usage for sidebar badge (gracefully degrade on errors/disabled)
  const aiUsageSummary = useAiStaffUsageSummary();

  const pinnedQueue = React.useMemo(
    () => pinnedSidebarIds.filter((id) => navItemMap.has(id) && id !== DASHBOARD_ROUTE),
    [navItemMap, pinnedSidebarIds],
  );

  const orderedBaseIds = React.useMemo(() => {
    const storedOrder = sidebarOrder.filter((id) => navItemMap.has(id) && id !== DASHBOARD_ROUTE);
    const fallbackIds = filteredNavItems
      .map((item) => item.to)
      .filter((id) => id !== DASHBOARD_ROUTE && !storedOrder.includes(id));
    return [...storedOrder, ...fallbackIds];
  }, [filteredNavItems, navItemMap, sidebarOrder]);

  const orderedNavItems = React.useMemo(() => {
    const assembled: NavItem[] = [];
    const dashboardItem = navItemMap.get(DASHBOARD_ROUTE);
    if (dashboardItem) {
      assembled.push(dashboardItem);
    }
    pinnedQueue.forEach((id) => {
      const item = navItemMap.get(id);
      if (item) {
        assembled.push(item);
      }
    });
    orderedBaseIds
      .filter((id) => !pinnedQueue.includes(id))
      .forEach((id) => {
        const item = navItemMap.get(id);
        if (item) {
          assembled.push(item);
        }
      });
    return assembled;
  }, [navItemMap, orderedBaseIds, pinnedQueue]);

  const sidebarNavItems = React.useMemo<SidebarNavItem[]>(
    () =>
      orderedNavItems.map((item) => {
        const base: SidebarNavItem = {
          id: item.to,
          label: translateLabel(item),
          icon: React.createElement(item.icon, { className: "h-4 w-4" }),
          contentKey: item.contentKey,
          href: resolveTenantPath(item.to),
          isPinned: pinnedQueue.includes(item.to),
          isFixed: item.to === DASHBOARD_ROUTE,
        };
        // Attach AI usage badge for AI Staff Copilot link when available
        if (item.to === "/ai-staff" && aiUsageSummary?.data) {
          const used = aiUsageSummary.data.chats ?? 0;
          const included = aiUsageSummary.data.includedChats;
          const badgeText = included !== null ? `${used}/${included}` : `${used}/∞`;
          return { ...base, badgeText };
        }
        return base;
      }),
    [orderedNavItems, pinnedQueue, resolveTenantPath, translateLabel, aiUsageSummary?.data],
  );

  const activeSidebarId = React.useMemo(() => {
    return activeItem?.to ?? orderedNavItems[0]?.to ?? navSource[0]?.to ?? DASHBOARD_ROUTE;
  }, [activeItem, navSource, orderedNavItems]);

  const sidebarDesktopWidthClass = isSidebarCollapsed ? "lg:w-16" : "lg:w-80 xl:w-96";
  const sidebarDesktopPaddingClass = isSidebarCollapsed ? "lg:pl-16" : "lg:pl-80 xl:pl-96";

  const handleToggleSidebarPin = React.useCallback(
    (id: string) => {
      if (id === DASHBOARD_ROUTE || !navItemMap.has(id)) {
        return;
      }
      setPinnedSidebarIds((previous) => {
        if (previous.includes(id)) {
          return previous.filter((value) => value !== id);
        }
        return [...previous, id];
      });
      setSidebarOrder((previous) => (previous.includes(id) ? previous : [...previous, id]));
    },
    [navItemMap],
  );

  const handleReorderSidebar = React.useCallback(
    (orderedIds: string[]) => {
      const availableSet = new Set(filteredNavItems.map((item) => item.to));
      const pinnedSet = new Set(pinnedSidebarIds);
      setSidebarOrder((previous) => {
        const preserved = previous
          .filter((id) => pinnedSet.has(id) || id === DASHBOARD_ROUTE)
          .filter((id) => id === DASHBOARD_ROUTE || availableSet.has(id));
        const sanitizedOrdered = orderedIds.filter(
          (id, index) =>
            availableSet.has(id) && !pinnedSet.has(id) && id !== DASHBOARD_ROUTE && orderedIds.indexOf(id) === index,
        );
        const combined = [...preserved, ...sanitizedOrdered];
        const appended = filteredNavItems
          .map((item) => item.to)
          .filter((id) => !combined.includes(id));
        const next = [...combined, ...appended];
        return arraysEqual(previous, next) ? previous : next;
      });
    },
    [filteredNavItems, pinnedSidebarIds],
  );

  const handleNavigate = React.useCallback(
    (to: string) => {
      const destination = resolveTenantPath(to);
      navigate(destination);
      if (typeof window === "undefined" || !window.matchMedia("(min-width: 1024px)").matches) {
        setIsSidebarVisible(false);
      }
    },
    [navigate, resolveTenantPath],
  );

  const logSidebarNavClick = React.useCallback((item: SidebarNavItem) => {
    if (typeof window === "undefined") {
      return;
    }
    const entry = {
      event: "dashboard.sidebar.nav_click",
      navId: item.id,
      label: item.label,
      href: item.href,
    };
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(entry);
    } else {
      window.dataLayer = [entry];
    }
  }, []);

  const handleSidebarSectionChange = React.useCallback(
    (item: SidebarNavItem) => {
      if (item.href) {
        logSidebarNavClick(item);
        handleNavigate(item.href);
      }
    },
    [handleNavigate, logSidebarNavClick],
  );

  // Wait for either Supabase or server session to settle before enforcing redirects
  const hasResolvedSession =
    session.dataUpdatedAt > 0 || session.status === "error" || Boolean(session.error);
  const isAuthenticating = authLoading || session.isFetching || session.loading || !hasResolvedSession;
  const hasAuthenticatedIdentity = session.authenticated || Boolean(user);

  React.useEffect(() => {
    if (isAuthenticating) {
      return;
    }
    if (!hasAuthenticatedIdentity) {
      navigate('/', { replace: true });
    }
  }, [hasAuthenticatedIdentity, isAuthenticating, navigate]);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const node = mainRef.current;
    if (node) {
      node.focus();
    }
  }, [pathname]);

  React.useEffect(() => {
    if (isPeekView) {
      return;
    }
    if (typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches) {
      return;
    }
    setIsSidebarVisible(false);
  }, [pathname, isPeekView]);

  React.useEffect(() => {
    if (isPeekView) {
      return;
    }
    const node = mainRef.current;
    if (!node) {
      return;
    }

    const handleScroll = () => {
      setIsHeaderElevated(node.scrollTop > 12);
    };

    handleScroll();
    node.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      node.removeEventListener("scroll", handleScroll);
    };
  }, [isPeekView]);

  if (isPeekView) {
    return (
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <main
          id="main-content"
          ref={mainRef}
          tabIndex={-1}
          className="flex-1 overflow-auto focus:outline-none"
        >
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen w-full bg-gradient-to-br from-white via-gray-50/40 to-white dark:from-gray-900 dark:via-gray-800/50 dark:to-gray-900">
      <SkipToContent />
      {isSidebarVisible && (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => {
            setIsSidebarVisible(false);
          }}
        />
      )}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-full max-w-[18rem] sm:max-w-[20rem] md:max-w-[22rem] transform transition-transform duration-300 lg:max-w-none lg:translate-x-0 lg:transform-none lg:transition-[width] lg:duration-300 lg:ease-out",
          sidebarDesktopWidthClass,
          isSidebarVisible ? "translate-x-0" : "-translate-x-full",
        )}
        aria-label="Primary navigation"
      >
        <TwoLevelSidebar
          items={sidebarNavItems}
          activeSection={activeSidebarId}
          onSectionChange={handleSidebarSectionChange}
          onDetailCollapseChange={setIsSidebarCollapsed}
          accountLabel={sidebarRestaurantName}
          accountSubtitle={sidebarAccountSubtitle}
          onTogglePin={handleToggleSidebarPin}
          onReorder={handleReorderSidebar}
        />
      </div>
      <div
        className={cn(
          "relative flex flex-1 flex-col bg-gradient-to-br from-white via-gray-50/50 to-white dark:from-gray-900 dark:via-gray-800/50 dark:to-gray-900 lg:transition-[padding] lg:duration-300 lg:ease-out",
          sidebarDesktopPaddingClass,
        )}
      >
        <div className="flex min-h-screen flex-col overflow-hidden">
          <header
            className={cn(
              "sticky top-0 z-50 transition-all duration-300",
              isHeaderElevated
                ? "border-b border-gray-200/70 bg-white/90 backdrop-blur-xl shadow-sm dark:border-gray-700/60 dark:bg-gray-900/85"
                : "border-b border-transparent bg-transparent shadow-none",
            )}
          >
            <div className={cn(
              "flex items-center justify-between gap-3 px-4 py-3 transition-all duration-300 sm:px-6",
              isHeaderElevated ? "" : "lg:px-8",
            )}
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={toggleSidebarVisibility}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200/70 bg-white/80 text-gray-700 shadow-sm transition-colors duration-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 dark:border-gray-700/60 dark:bg-gray-900/80 dark:text-gray-200 dark:hover:bg-gray-800 lg:hidden"
                  aria-label={t("navigation.toggleSidebar", { defaultValue: "Toggle sidebar" })}
                >
                  <Menu className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                    {activeItem?.label ?? "Dashboard"}
                  </span>
                  <span className="hidden items-center gap-2 text-sm text-gray-500 sm:flex dark:text-gray-400">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    <span>{t("status.systemOnline")}</span>
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:flex-nowrap md:justify-end">
                <TenantSwitcher variant="icon" className="md:hidden" />
                <TenantSwitcher className="hidden md:inline-flex" />
                <LanguageToggle />
                <SignOutButton />
                <ThemeToggle />
              </div>
            </div>
            <div
              className={cn(
                "px-4 py-2 text-xs text-gray-600 transition-all duration-300 sm:px-6",
                isHeaderElevated
                  ? "border-t border-gray-200/60 bg-white/80 dark:border-gray-700/60 dark:bg-gray-900/80 dark:text-gray-300"
                  : "border-t border-transparent bg-transparent text-gray-600 dark:text-gray-300",
              )}
            >
              <div className="flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  {sidebarRestaurantName}
                </span>
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-wide text-[11px] text-gray-500 dark:text-gray-400">
                    {t("sidebar.statusLabel")}
                  </span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">{sidebarStatusText}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-wide text-[11px] text-gray-500 dark:text-gray-400">
                    {sidebarStaffLabel}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{sidebarStaffText}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-wide text-[11px] text-gray-500 dark:text-gray-400">
                    {t("sidebar.uptimeLabel")}
                  </span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">{sidebarUptimeText}</span>
                </span>
              </div>
            </div>
          </header>
          <main
            id="main-content"
            ref={mainRef}
            tabIndex={-1}
            className="relative flex-1 overflow-auto focus:outline-none"
          >
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:20px_20px] opacity-80 dark:bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)]" />
            <div className="relative z-10 px-4 py-6 sm:px-6">
              {isBackofficeReadOnly && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 shadow-sm dark:border-amber-400/40 dark:bg-amber-900/30 dark:text-amber-100">
                  {t("status.maintenanceReadOnly")}
                </div>
              )}
              {children}
            </div>
          </main>
          <footer className="border-t border-gray-200/70 bg-white/92 px-4 py-3 text-xs text-gray-500 shadow-inner dark:border-gray-700/60 dark:bg-gray-900/90 dark:text-gray-400 sm:px-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <span>&copy; {new Date().getFullYear()} Braise. {t("footer.rightsReserved")}</span>
                <Badge
                  variant="outline"
                  className="border-gray-200/70 bg-gray-100/70 text-gray-600 dark:border-gray-700/60 dark:bg-gray-800/60 dark:text-gray-300"
                >
                  {uiVersionLabel}
                </Badge>
              </div>
              <span className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
                <span>{t("status.liveSupport")}</span>
              </span>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

const DashboardLayoutV3 = ({ children }: DashboardLayoutV3Props): React.ReactElement => {
  useTenantProfileRealtime();

  return <DashboardLayoutChrome>{children}</DashboardLayoutChrome>;
};

export default DashboardLayoutV3;
